// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [unitsToggle] - The MM/IN measurement toggle (Size axis only).
 */

/** @typedef {'imperial' | 'metric'} Unit */

/**
 * Handles selection for variant axes and choice grids through the customizer store.
 * The Size axis also owns its MM/IN label toggle; variants resolve Shopify images, choices do not.
 *
 * @extends Component<Refs>
 */
class CustomizerSelector extends Component {
  /** The displayed unit. Imperial matches the server-rendered labels. @type {Unit} */
  #unit = 'imperial';

  /** The (translated) metric unit suffix, read from the toggle markup. @type {string} */
  #metricUnit = 'MM';

  connectedCallback() {
    super.connectedCallback();
    this.#initUnits();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#syncChecked);
    this.#syncChecked();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncChecked);
  }

  /** Mirrors checked state from the store, including skips, resets, and Back navigation. */
  #syncChecked = () => {
    const stepId = this.dataset.stepId;
    if (!stepId) return;

    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    const store = host?.store;
    const isVariant = this.dataset.kind !== 'choice';
    // Read this grid's control, not step input: multi-grid birthstone steps retain sibling picks.
    const controlKey = this.dataset.controlKey;
    const value =
      !isVariant && controlKey
        ? (store?.getControlValue(controlKey) ?? null)
        : (store?.steps?.[stepId]?.userInput ?? null);

    for (const candidate of this.querySelectorAll('[data-value]')) {
      const optionValue = candidate.getAttribute('data-value');
      candidate.setAttribute('aria-checked', String(value !== null && optionValue === value));

      // Disable combinations unavailable with the other selected variant axes.
      if (isVariant && store && optionValue !== null) {
        const unavailable = !store.isVariantValueAvailable(stepId, optionValue);
        candidate.classList.toggle('custom-customizer-selector__row--unavailable', unavailable);
        candidate.toggleAttribute('disabled', unavailable);
        candidate.setAttribute('aria-disabled', String(unavailable));
        const quote = store.variantValuePrice(stepId, optionValue);
        const price = candidate.querySelector('.custom-customizer-selector__price');
        price?.toggleAttribute('hidden', quote === null);
        if (quote) {
          const amount = candidate.querySelector('.custom-customizer-selector__price-amount');
          if (amount) {
            amount.textContent = formatMoney(
              quote.price,
              this.dataset.moneyFormat || '{{amount}}',
              this.dataset.currency || 'USD'
            );
          }
          candidate
            .querySelector('.custom-customizer-selector__price-prefix')
            ?.toggleAttribute('hidden', !quote.varies);
        }
      }
    }
  };

  /**
   * Records a selection and lets {@link #syncChecked} reflect the store state.
   * Choice grids deselect on a second click; variant axes use the required `setVariant()` path.
   * @param {Event} event
   */
  select(event) {
    const target = /** @type {Element} */ (event.target);
    const row = target.closest('[data-value]');
    if (!(row instanceof HTMLElement)) return;
    if (row.hasAttribute('disabled')) return;

    const stepId = this.dataset.stepId;
    if (!stepId) return;

    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    const store = host?.store;
    if (!store) return;

    const value = row.dataset.value ?? null;
    if (this.dataset.kind === 'choice') {
      // Per-grid read, as in `#syncChecked`: a sibling grid's stone must not be cleared from here.
      const controlKey = this.dataset.controlKey;
      const current = controlKey
        ? store.getControlValue(controlKey)
        : (store.steps?.[stepId]?.userInput ?? null);
      const next = current === value ? null : value;
      // The CONTROL value is what the stone adapter, `stepSurchargeCents()` and
      // `cartProperties()` read; `userInput` is only the progress signal (see `updateChoice`).
      store.updateChoice(stepId, controlKey, next);
    } else {
      store.setVariant(stepId, { value });
    }
  }

  /** Caches metric labels and hides the toggle when no label is a measurement. */
  #initUnits() {
    const container = this.querySelector('.custom-customizer-selector__units');
    if (!(container instanceof HTMLElement)) return;

    const metricOption = this.refs.unitsToggle?.querySelector('[data-unit="metric"]');
    this.#metricUnit = metricOption?.textContent?.trim() || this.#metricUnit;

    let anyConvertible = false;
    for (const label of this.querySelectorAll('.custom-customizer-selector__label')) {
      if (!(label instanceof HTMLElement)) continue;
      const imperial = label.dataset.imperial ?? label.textContent ?? '';
      const metric = this.#toMetric(imperial);
      if (metric) {
        label.dataset.metric = metric;
        anyConvertible = true;
      }
    }

    if (!anyConvertible) container.hidden = true;
  }

  /** Toggle size labels between imperial and metric. Bound via `on:click='/toggleUnits'`; updates
   *  each label from cached `data-imperial`/`data-metric`, reflects unit on `aria-pressed`. */
  toggleUnits() {
    const { unitsToggle } = this.refs;
    if (!unitsToggle) return;

    this.#unit = this.#unit === 'imperial' ? 'metric' : 'imperial';
    unitsToggle.setAttribute('aria-pressed', String(this.#unit === 'metric'));

    for (const label of this.querySelectorAll('.custom-customizer-selector__label')) {
      if (!(label instanceof HTMLElement)) continue;
      const { imperial, metric } = label.dataset;
      const next = this.#unit === 'metric' ? metric : imperial;
      if (next !== undefined) label.textContent = next;
    }
  }

  /**
   * Converts imperial size labels to rounded millimeters: whole, fractional and mixed dimensions.
   * Dimensions may use `x` or `×`; for example, `"3/4 x 3/4 IN"` becomes `"19 × 19 MM"`.
   * @param {string} imperial
   * @returns {string | null} The metric label, or `null` when the input isn't a measurement.
   */
  #toMetric(imperial) {
    const stripped = imperial.replace(/\s*(?:in|inch|inches|")\s*$/i, '').trim();
    if (!stripped) return null;

    const parts = stripped.split(/\s*[x×]\s*/i);
    /** @type {string[]} */
    const millimeters = [];
    for (const part of parts) {
      const inches = this.#parseInches(part);
      if (inches === null) return null;
      millimeters.push(String(Math.round(inches * 25.4)));
    }

    return `${millimeters.join(' × ')} ${this.#metricUnit}`;
  }

  /**
   * Parse a single imperial dimension (`"1"`, `"3/4"`, or `"1 1/4"`) into a decimal inch value.
   * @param {string} token
   * @returns {number | null} Inches, or `null` if any component isn't a finite number.
   */
  #parseInches(token) {
    const pieces = token.trim().split(/\s+/).filter(Boolean);
    if (pieces.length === 0) return null;

    let total = 0;
    for (const piece of pieces) {
      const [num, den] = piece.split('/');
      const numerator = Number(num);
      const denominator = den === undefined ? 1 : Number(den);
      if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
        return null;
      }
      total += numerator / denominator;
    }
    return total;
  }
}

if (!customElements.get('custom-customizer-selector')) {
  customElements.define('custom-customizer-selector', CustomizerSelector);
}
